import { Injectable, OnDestroy, inject } from "@angular/core";
import { Subscription } from "rxjs";
import { filter, tap } from "rxjs/operators";

import { Context, LOCALE, Settings } from "@hermes/core-app-core";
import { isChina } from "@hermes/locale";
import { FeatureFlagFacade, SENSORS_DATA } from "@hermes/hermes-web-states-flipper-state";
import { LOGGER } from "@hermes/core-web-logger";

@Injectable()
export class SensorsDataService implements OnDestroy {
    #featureFlagFacade = inject(FeatureFlagFacade);
    private context = inject(Context);
    private logger = inject(LOGGER);
    private locale = inject(LOCALE);
    private settings = inject(Settings);
    private subscription: Subscription = new Subscription();
    private sensorsUrl: string | undefined = this.settings.sensorsConfiguration;

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    private async loadSensorsSDK(): Promise<
        { init: (config: Record<string, unknown>) => void } | undefined
    > {
        try {
            const sensorsModule = await import("sa-sdk-javascript");
            return sensorsModule.default;
        } catch (error) {
            this.logger.error("Failed to load SensorsData SDK", error);
            return undefined;
        }
    }

    public executeService(): void {
        if (!this.context.isInBrowserMode()) {
            return;
        }

        this.subscription.add(
            this.#featureFlagFacade
                .isActivated(SENSORS_DATA)
                .pipe(
                    filter((isActivated: boolean) => isActivated),
                    filter(
                        () =>
                            this.sensorsUrl !== undefined &&
                            isChina(this.locale),
                    ),
                    tap(async () => {
                        try {
                            this.logger.info("Loading SensorsData SDK...");
                            const sensors = await this.loadSensorsSDK();
                            if (sensors) {
                                sensors.init({
                                    server_url: this.sensorsUrl,
                                });
                                this.logger.info(
                                    "SensorsData initialized successfully",
                                );
                            }
                        } catch (error) {
                            this.logger.error(
                                "Failed to initialize SensorsData",
                                error,
                            );
                        }
                    }),
                )
                .subscribe(),
        );
    }
}
